import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDocumentCacheManager } from 'entities/document/api/hooks/use-document-cache-manager';
import { handleBatchTask } from 'shared/api/batches';
import { queryKey } from '../../../document/api/hooks';
import {
  type PutDocumentsQueueRequestType,
  putDocumentsQueue,
} from '../../api';

export type UsePutDocumentsQueueProps = Pick<
  PutDocumentsQueueRequestType,
  'documentId'
>;

export type MutationParam = Pick<PutDocumentsQueueRequestType, 'documentQueue'>;

/**
 * Custom hook to put documents queue.
 *
 * @param props - Hook props.
 */
export const usePutDocumentsQueue = (props: UsePutDocumentsQueueProps) => {
  const queryClient = useQueryClient();
  const { invalidateDocuments } = useDocumentCacheManager();

  return useMutation({
    mutationFn: (param: MutationParam) =>
      handleBatchTask(
        putDocumentsQueue({
          ...props,
          ...param,
        }),
      ),
    onSuccess: () => {
      // This mutation only persists the queue (signers/acceptors), not groups.
      // Group invalidation is owned by usePutDocumentsWorkflow, which runs it
      // after the group create/update calls — invalidating here would refetch
      // groups before they are persisted.
      invalidateDocuments({
        documentIds: [props.documentId],
        includeQueue: true,
        includeQueueGroup: false,
      });
      queryClient.refetchQueries(queryKey.document(props.documentId));
      queryClient.refetchQueries(queryKey.documentsVersions(props.documentId));
      queryClient.refetchQueries(queryKey.documentComments(props.documentId));
    },
  });
};
